import axios from 'axios';
import store from '@/store';
import { validateToken } from './tokenValidator';

// Flag to track token expiration handling
let tokenExpirationHandled = false;

// Create a base axios instance with default config
const axiosClient = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to set authorization header when needed
const setAuthorizationHeader = (config) => {
  const bearerToken = localStorage.getItem('bearer_token');
  if (bearerToken) {
    config.headers['Authorization'] = `Bearer ${bearerToken}`;
  }
  return config;
};

// Request interceptor - adds auth header and validates token
axiosClient.interceptors.request.use(
  async (config) => {
    // Only validate token if request requires auth
    if (config.requireAuth) {
      await validateToken();
      // Set authorization header only for auth-required requests
      return setAuthorizationHeader(config);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles token expiration
axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Check if it's an authentication error (401)
    if (error.response && error.response.status === 401) {
      // Get the baseURL and url from the error config
      let baseURL = error.config.baseURL ?? '';
      let url = error.config.url ?? '';
      let fullUrl = baseURL + url;

      // Only handle 401s from our API and prevent duplicate handling
      if (fullUrl.includes(import.meta.env.VITE_BASE_URL) && !tokenExpirationHandled) {
        tokenExpirationHandled = true;
        
        try {
          // Display a warning message to the user
          if (store && store.dispatch) {
            store.dispatch('handleExpiredSession');
          }
        } catch (error) {
          setTimeout(() => { window.location.href = 'https://vlifemedia.com'; }, 2000);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Reset the token expiration flag when the page is reloaded or focused
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    tokenExpirationHandled = false;
  });
  
  window.addEventListener('focus', () => {
    tokenExpirationHandled = false;
  });
}

/**
 * Helper function to make a GET request
 * @param {string} url - The URL to make the request to
 * @param {Object} params - URL parameters to include in the request
 * @param {Object} config - Additional axios config
 * @param {boolean} config.requireAuth - Whether this request requires authentication
 * @returns {Promise} - The axios promise
 */
export const get = (url, params = {}, config = {}) => {
  return new Promise((resolve, reject) => {
    axiosClient({
      method: 'GET',
      url,
      params,
      requireAuth: config.requireAuth ?? false,
      ...config
    })
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        // Preserve the full error object with response status
        if (err.response) {
          // Create an error object that includes status and data
          const enhancedError = {
            response: {
              status: err.response.status,
              data: err.response.data
            },
            status_code: err.response.status, // Add this for backward compatibility
            data: err.response.data // Add this for backward compatibility
          };
          reject(enhancedError);
        } else {
          reject(err);
        }
      });
  });
};

/**
 * Helper function to make a POST request
 * @param {string} url - The URL to make the request to
 * @param {Object} data - The data to send in the request body
 * @param {Object} config - Additional axios config
 * @param {boolean} config.requireAuth - Whether this request requires authentication
 * @returns {Promise} - The axios promise
 */
export const post = (url, data = {}, config = {}) => {
  return new Promise((resolve, reject) => {
    axiosClient({
      method: 'POST',
      url,
      data,
      requireAuth: config.requireAuth ?? false,
      ...config
    })
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        // Preserve the full error object with response status
        if (err.response) {
          // Create an error object that includes status and data
          const enhancedError = {
            response: {
              status: err.response.status,
              data: err.response.data
            },
            status_code: err.response.status, // Add this for backward compatibility
            data: err.response.data // Add this for backward compatibility
          };
          reject(enhancedError);
        } else {
          reject(err);
        }
      });
  });
};

/**
 * Helper function to make a PATCH request
 * @param {string} url - The URL to make the request to
 * @param {Object} data - The data to send in the request body
 * @param {Object} config - Additional axios config
 * @param {boolean} config.requireAuth - Whether this request requires authentication
 * @returns {Promise} - The axios promise
 */
export const patch = (url, data = {}, config = {}) => {
  return new Promise((resolve, reject) => {
    axiosClient({
      method: 'PATCH',
      url,
      data,
      requireAuth: config.requireAuth ?? false,
      ...config
    })
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        // Preserve the full error object with response status
        if (err.response) {
          // Create an error object that includes status and data
          const enhancedError = {
            response: {
              status: err.response.status,
              data: err.response.data
            },
            status_code: err.response.status, // Add this for backward compatibility
            data: err.response.data // Add this for backward compatibility
          };
          reject(enhancedError);
        } else {
          reject(err);
        }
      });
  });
};

/**
 * Helper function to make a PUT request
 * @param {string} url - The URL to make the request to
 * @param {Object} data - The data to send in the request body
 * @param {Object} config - Additional axios config
 * @param {boolean} config.requireAuth - Whether this request requires authentication
 * @returns {Promise} - The axios promise
 */
export const put = (url, data = {}, config = {}) => {
  return new Promise((resolve, reject) => {
    axiosClient({
      method: 'PUT',
      url,
      data,
      requireAuth: config.requireAuth ?? false,
      ...config
    })
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        // Preserve the full error object with response status
        if (err.response) {
          // Create an error object that includes status and data
          const enhancedError = {
            response: {
              status: err.response.status,
              data: err.response.data
            },
            status_code: err.response.status, // Add this for backward compatibility
            data: err.response.data // Add this for backward compatibility
          };
          reject(enhancedError);
        } else {
          reject(err);
        }
      });
  });
};

/**
 * Helper function to make a DELETE request
 * @param {string} url - The URL to make the request to
 * @param {Object} config - Additional axios config
 * @param {boolean} config.requireAuth - Whether this request requires authentication
 * @returns {Promise} - The axios promise
 */
export const deleteRequest = (url, config = {}) => {
  return new Promise((resolve, reject) => {
    axiosClient({
      method: 'DELETE',
      url,
      requireAuth: config.requireAuth ?? false,
      ...config
    })
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        // Preserve the full error object with response status
        if (err.response) {
          // Create an error object that includes status and data
          const enhancedError = {
            response: {
              status: err.response.status,
              data: err.response.data
            },
            status_code: err.response.status, // Add this for backward compatibility
            data: err.response.data // Add this for backward compatibility
          };
          reject(enhancedError);
        } else {
          reject(err);
        }
      });
  });
};

// Export the raw axiosClient for advanced use cases
export default axiosClient;